import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_GLOBAL_ACCESS_TOKEN_FAIL } from "@redux/constants/action-types";
import { getGlobalAccessToken } from "@redux/actions/utility-actions";
import { forgotPasswordApi } from "../services/auth.service";
import { mailformat } from "../utils/validation";

/**
 * Hook to handle password reset logic
 * @returns {Object} Password reset state and handlers
 */
export const usePasswordReset = () => {
  const [emailId, setEmailId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState("");
  const dispatch = useDispatch();

  // Select globalAccessToken from Redux with custom equality check
  const globalAccessToken = useSelector(
    (state) => state?.tokens?.globalAccessToken,
    (prev, next) => prev === next
  );

  // Memoized email validation
  const validateEmail = useCallback((email) => {
    if (!email) return "Email ID is required";
    if (!email.match(mailformat)) return "Please enter valid email address";
    return "";
  }, []);

  // Memoized reset handler
  const handleReset = useCallback(async (e) => {
    e?.preventDefault();
    const error = validateEmail(emailId);
    
    if (error) {
      setValidationError(error);
      return;
    }

    setIsResetClicked(true);
    setValidationError("");
  }, [emailId, validateEmail]);

  // Fetch global access token
  useEffect(() => {
    if (isResetClicked && !validationError) {
      (async () => {
        setLoading(true);
        setShowModal(false);
        await dispatch(getGlobalAccessToken());
        setIsResetClicked(false);
      })();
    }
  }, [isResetClicked, validationError, dispatch]);

  // Call forgot password API
  useEffect(() => {
    if (!globalAccessToken || !globalAccessToken.trim()) return;

    (async () => {
      const response = await forgotPasswordApi({ globalAccessToken, emailId });
      setLoading(false);
      dispatch({ type: GET_GLOBAL_ACCESS_TOKEN_FAIL });

      if (response?.success) {
        setIsResetSuccess(true);
        setValidationError("");
        setEmailId("");
      } else {
        setError(response?.data);
      }
    })();
  }, [globalAccessToken, dispatch, emailId]);

  return {
    emailId,
    setEmailId,
    showModal,
    setShowModal,
    isResetSuccess,
    setIsResetSuccess,
    loading,
    setLoading,
    error,
    setError,
    validationError,
    setValidationError,
    handleReset,
  };
};
